import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  FileText, 
  Loader2, 
  CheckCircle2, 
  XCircle, 
  Clock,
  Eye,
  Plus,
  Search,
  RefreshCw,
  FileSignature,
  Users,
  Send,
  Ban,
  History,
  ChevronRight,
  AlertCircle
} from 'lucide-react';

const AdminSignaturesPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('requests');
  
  // New request dialog
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newRequest, setNewRequest] = useState({
    template_id: '',
    user_id: '',
    opportunity_id: '',
    message: ''
  });
  const [creating, setCreating] = useState(false);

  // Detail dialog
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [requestsRes, templatesRes, usersRes, oppsRes] = await Promise.all([
        api.get('/admin/signature-requests'),
        api.get('/admin/signature-templates'),
        api.get('/admin/users'),
        api.get('/opportunities')
      ]);
      setRequests(requestsRes.data.requests || []);
      setTemplates(templatesRes.data.templates || []);
      setUsers(usersRes.data.users || []);
      setOpportunities(oppsRes.data.opportunities || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = async () => {
    if (!newRequest.template_id || !newRequest.user_id) return;
    
    setCreating(true);
    try {
      const payload = {
        template_id: newRequest.template_id,
        user_id: newRequest.user_id,
        opportunity_id: newRequest.opportunity_id || null,
        message: newRequest.message || null
      };
      
      await api.post('/admin/signature-requests', payload);
      setShowNewDialog(false);
      setNewRequest({ template_id: '', user_id: '', opportunity_id: '', message: '' });
      fetchData();
    } catch (error) {
      console.error('Error creating request:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleViewDetail = async (requestId) => {
    setDetailLoading(true);
    setShowDetailDialog(true);
    try {
      const response = await api.get(`/admin/signature-requests/${requestId}`);
      setSelectedRequest(response.data);
    } catch (error) {
      console.error('Error fetching detail:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleResend = async (requestId) => {
    try {
      await api.post(`/admin/signature-requests/${requestId}/resend`);
      alert('Recordatorio enviado');
    } catch (error) {
      console.error('Error resending:', error);
    }
  };

  const handleCancel = async (requestId) => {
    const reason = prompt('Motivo de cancelación:');
    if (!reason) return;
    
    try {
      await api.post(`/admin/signature-requests/${requestId}/cancel`, { reason });
      fetchData();
      setShowDetailDialog(false);
    } catch (error) {
      console.error('Error cancelling:', error);
    }
  };

  const handleComplete = async (requestId) => {
    try {
      await api.post(`/admin/signature-requests/${requestId}/complete`);
      fetchData();
      setShowDetailDialog(false);
    } catch (error) {
      console.error('Error completing:', error);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { className: 'bg-amber-500/10 text-amber-500 border-amber-500/20', label: 'Pendiente' },
      viewed: { className: 'bg-blue-500/10 text-blue-500 border-blue-500/20', label: 'Visto' },
      signed: { className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', label: 'Firmado' },
      completed: { className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', label: 'Completado' },
      rejected: { className: 'bg-red-500/10 text-red-500 border-red-500/20', label: 'Rechazado' },
      expired: { className: 'bg-muted text-muted-foreground border-muted', label: 'Expirado' },
      cancelled: { className: 'bg-muted text-muted-foreground border-muted', label: 'Cancelado' },
    };
    const style = styles[status] || styles.pending;
    return <Badge variant="outline" className={style.className}>{style.label}</Badge>;
  };

  const filteredRequests = requests.filter(r => {
    const matchesSearch = !searchTerm || 
      r.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.document_reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.template_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || r.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Stats
  const stats = {
    total: requests.length,
    pending: requests.filter(r => ['pending', 'viewed'].includes(r.status)).length,
    signed: requests.filter(r => r.status === 'signed').length,
    completed: requests.filter(r => r.status === 'completed').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Gestión de Firmas</h1>
          <p className="text-muted-foreground mt-1">
            Administre solicitudes de firma y plantillas de documentos
          </p>
        </div>
        <Button className="btn-primary" onClick={() => setShowNewDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Nueva Solicitud
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="card-dashboard">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <FileSignature className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{stats.total}</p>
                <p className="text-sm text-muted-foreground">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Clock className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{stats.pending}</p>
                <p className="text-sm text-muted-foreground">Pendientes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <FileText className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{stats.signed}</p>
                <p className="text-sm text-muted-foreground">Firmados</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{stats.completed}</p>
                <p className="text-sm text-muted-foreground">Completados</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por usuario, email o referencia..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="pending">Pendiente</SelectItem>
            <SelectItem value="viewed">Visto</SelectItem>
            <SelectItem value="signed">Firmado</SelectItem>
            <SelectItem value="completed">Completado</SelectItem>
            <SelectItem value="rejected">Rechazado</SelectItem>
            <SelectItem value="expired">Expirado</SelectItem>
            <SelectItem value="cancelled">Cancelado</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Requests Table */}
      <Card className="card-dashboard">
        <CardHeader>
          <CardTitle className="text-lg">Solicitudes de Firma</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredRequests.length === 0 ? (
            <div className="text-center py-8">
              <FileSignature className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No hay solicitudes que coincidan con los filtros.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredRequests.map((req) => (
                <div 
                  key={req.request_id}
                  className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => handleViewDetail(req.request_id)}
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <FileText className="h-4 w-4 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium truncate">{req.template_name}</p>
                        {getStatusBadge(req.status)}
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        {req.user_name} ({req.user_email}) • Ref: {req.document_reference}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-sm">{new Date(req.created_at).toLocaleDateString('es-MX')}</p>
                      {req.signed_at && (
                        <p className="text-xs text-muted-foreground">
                          Firmado: {new Date(req.signed_at).toLocaleDateString('es-MX')}
                        </p>
                      )}
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Templates Section */}
      <Card className="card-dashboard">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">Plantillas de Documentos</CardTitle>
            <CardDescription>Plantillas disponibles para solicitudes de firma</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {templates.map((tpl) => (
              <Card key={tpl.template_id} className="border border-border">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-muted">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{tpl.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Tipo: {tpl.template_type}
                      </p>
                      <Badge variant="outline" className={`mt-2 ${tpl.is_active ? 'text-emerald-500' : 'text-muted-foreground'}`}>
                        {tpl.is_active ? 'Activa' : 'Inactiva'}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* New Request Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Nueva Solicitud de Firma</DialogTitle>
            <DialogDescription>
              Cree una nueva solicitud de firma para un usuario.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Plantilla *</label>
              <Select value={newRequest.template_id} onValueChange={(v) => setNewRequest(prev => ({...prev, template_id: v}))}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccione una plantilla" />
                </SelectTrigger>
                <SelectContent>
                  {templates.filter(t => t.is_active).map((tpl) => (
                    <SelectItem key={tpl.template_id} value={tpl.template_id}>
                      {tpl.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium">Usuario *</label>
              <Select value={newRequest.user_id} onValueChange={(v) => setNewRequest(prev => ({...prev, user_id: v}))}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccione un usuario" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.user_id} value={u.user_id}>
                      {u.name} ({u.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium">Oportunidad (opcional)</label>
              <Select value={newRequest.opportunity_id} onValueChange={(v) => setNewRequest(prev => ({...prev, opportunity_id: v}))}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccione una oportunidad" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Ninguna</SelectItem>
                  {opportunities.map((opp) => (
                    <SelectItem key={opp.opportunity_id} value={opp.opportunity_id}>
                      {opp.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium">Mensaje (opcional)</label>
              <Input
                value={newRequest.message}
                onChange={(e) => setNewRequest(prev => ({...prev, message: e.target.value}))}
                placeholder="Mensaje personalizado para el usuario"
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDialog(false)}>
              Cancelar
            </Button>
            <Button 
              className="btn-primary"
              onClick={handleCreateRequest}
              disabled={!newRequest.template_id || !newRequest.user_id || creating}
            >
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creando...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Crear y Enviar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : selectedRequest && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle>{selectedRequest.template_name}</DialogTitle>
                  {getStatusBadge(selectedRequest.status)}
                </div>
                <DialogDescription>
                  Referencia: {selectedRequest.document_reference}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                {/* User Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Usuario</p>
                    <p className="font-medium">{selectedRequest.user_name}</p>
                    <p className="text-sm text-muted-foreground">{selectedRequest.user_email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Oportunidad</p>
                    <p className="font-medium">{selectedRequest.opportunity_title || 'N/A'}</p>
                  </div>
                </div>

                {/* Dates */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Creado</p>
                    <p className="font-medium">{new Date(selectedRequest.created_at).toLocaleString('es-MX')}</p>
                  </div>
                  {selectedRequest.viewed_at && (
                    <div>
                      <p className="text-sm text-muted-foreground">Visto</p>
                      <p className="font-medium">{new Date(selectedRequest.viewed_at).toLocaleString('es-MX')}</p>
                    </div>
                  )}
                  {selectedRequest.signed_at && (
                    <div>
                      <p className="text-sm text-muted-foreground">Firmado</p>
                      <p className="font-medium">{new Date(selectedRequest.signed_at).toLocaleString('es-MX')}</p>
                    </div>
                  )}
                </div>

                {/* Consent Info */}
                {selectedRequest.electronic_consent_given && (
                  <Card className="border-emerald-500/20 bg-emerald-500/5">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5" />
                        <div>
                          <p className="font-medium text-emerald-600">Consentimiento Electrónico Otorgado</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(selectedRequest.electronic_consent_at).toLocaleString('es-MX')}
                          </p>
                          <p className="text-sm text-muted-foreground">IP: {selectedRequest.electronic_consent_ip}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Signature Info */}
                {selectedRequest.signed_at && (
                  <Card className="border-emerald-500/20 bg-emerald-500/5">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <FileSignature className="h-5 w-5 text-emerald-500 mt-0.5" />
                        <div>
                          <p className="font-medium text-emerald-600">Documento Firmado</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(selectedRequest.signed_at).toLocaleString('es-MX')}
                          </p>
                          <p className="text-sm text-muted-foreground">IP: {selectedRequest.signed_ip}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Audit Trail */}
                {selectedRequest.audit_trail && selectedRequest.audit_trail.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-3 flex items-center gap-2">
                      <History className="h-4 w-4" />
                      Historial de Auditoría
                    </p>
                    <div className="space-y-2">
                      {selectedRequest.audit_trail.map((audit, idx) => (
                        <div key={idx} className="flex items-start gap-3 text-sm">
                          <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                          <div>
                            <p>
                              <span className="font-medium uppercase">{audit.action}</span>
                              {audit.performed_by_name && <span className="text-muted-foreground"> por {audit.performed_by_name}</span>}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(audit.created_at).toLocaleString('es-MX')}
                              {audit.ip_address && ` • IP: ${audit.ip_address}`}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <DialogFooter className="gap-2">
                {['pending', 'viewed'].includes(selectedRequest.status) && (
                  <>
                    <Button variant="outline" onClick={() => handleResend(selectedRequest.request_id)}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Reenviar
                    </Button>
                    <Button 
                      variant="outline" 
                      className="text-red-500"
                      onClick={() => handleCancel(selectedRequest.request_id)}
                    >
                      <Ban className="h-4 w-4 mr-2" />
                      Cancelar
                    </Button>
                  </>
                )}
                {selectedRequest.status === 'signed' && (
                  <Button className="btn-primary" onClick={() => handleComplete(selectedRequest.request_id)}>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Marcar como Completado
                  </Button>
                )}
                <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
                  Cerrar
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSignaturesPage;
